import React, { useState, useEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import MentionInput from '../../components/MentionInput/MentionInput';
import MentionText from '../../components/MentionInput/MentionText';

interface Comment {
    id: number;
    comment: string;
    user: { id: number; name: string; initials: string };
    isMine: boolean;
    createdAt: string;
    createdAtRelative: string;
}

interface MeasureCommentsProps {
    measureId: number;
    measuresActions: any;
}

const AVATAR_COLORS = ['#3b82f6', '#8b5cf6', '#06b6d4', '#f59e0b', '#ef4444', '#22c55e', '#ec4899', '#6366f1'];

const getAvatarColor = (name: string) => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const getInitials = (name: string) => {
    return name.split(' ').map(w => w.charAt(0)).join('').substring(0, 2).toUpperCase() || '??';
};

const MeasureComments: React.FC<MeasureCommentsProps> = ({ measureId, measuresActions }) => {
    const intl = useIntl();
    const fm = (id: string, defaultMessage: string) => intl.formatMessage({ id, defaultMessage });

    const [comments, setComments] = useState<Comment[]>([]);
    const [newComment, setNewComment] = useState('');
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const listRef = useRef<HTMLDivElement>(null);

    const fetchComments = () => {
        setLoading(true);
        measuresActions.getMeasureComments(measureId).then((res: any) => {
            const data = res?.data?.data || res?.data || [];
            if (Array.isArray(data)) setComments(data);
            setLoading(false);
            setTimeout(() => {
                if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
            }, 50);
        });
    };

    useEffect(() => {
        if (measureId) fetchComments();
    }, [measureId]);

    const handleSend = () => {
        if (!newComment.trim() || sending) return;
        setSending(true);
        measuresActions.addMeasureComment(measureId, newComment.trim()).then((res: any) => {
            const comment = res?.data?.data || res?.data;
            if (comment?.id) {
                setComments(prev => [...prev, comment]);
                setNewComment('');
                setTimeout(() => {
                    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
                }, 50);
            }
            setSending(false);
        });
    };

    const handleDelete = (commentId: number) => {
        measuresActions.deleteMeasureComment(commentId).then((res: any) => {
            const success = res?.data?.data ?? res?.data;
            if (success) setComments(prev => prev.filter(c => c.id !== commentId));
        });
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    return (
        <div className="mc-container">
            <div className="mc-header">
                <FormattedMessage id="measure.comments.title" defaultMessage="Comments" />
                {comments.length > 0 && <span className="mc-count">{comments.length}</span>}
            </div>
            <div className="mc-list" ref={listRef}>
                {loading && comments.length === 0 ? (
                    <div className="mc-empty">
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" /></svg>
                        <span><FormattedMessage id="measure.comments.loading" defaultMessage="Loading..." /></span>
                    </div>
                ) : comments.length === 0 ? (
                    <div className="mc-empty">
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" /></svg>
                        <span><FormattedMessage id="measure.comments.noComments" defaultMessage="No comments yet" /></span>
                    </div>
                ) : (
                    comments.map(c => {
                        const userName = c.user?.name || '??';
                        const initials = c.user?.initials || getInitials(userName);
                        const avatarColor = getAvatarColor(userName);
                        return (
                            <div key={c.id} className="mc-item">
                                <div className="mc-avatar" style={{ background: avatarColor }}>{initials}</div>
                                <div className="mc-content">
                                    <div className="mc-meta">
                                        <span className="mc-item-user">{userName}</span>
                                        <span className="mc-item-dot">&middot;</span>
                                        <span className="mc-item-time" title={c.createdAt}>{c.createdAt}</span>
                                    </div>
                                    <p className="mc-item-text"><MentionText text={c.comment} /></p>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
            <div className="mc-input-wrap">
                {/* MentionInput in textarea mode — drops in for the previous
                    <textarea> and adds @ autocomplete. onKeyDown still fires
                    for Enter-to-send when no mention popover is open. */}
                <MentionInput
                    multiline
                    value={newComment}
                    onChange={(next) => setNewComment(next)}
                    onKeyDown={handleKeyDown}
                    placeholder={fm('measure.comments.placeholder', 'Write a comment...')}
                    rows={2}
                />
                <button className="mc-send" onClick={handleSend} disabled={!newComment.trim() || sending} type="button" title={fm('measure.comments.send', 'Send')}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>
                </button>
            </div>
        </div>
    );
};

export default MeasureComments;
